import React, {useState, useEffect} from 'react'
import {getDownloadLink} from './core/_requests'
import {initialQueryRequest} from './core/crud-helper'
import Swal from 'sweetalert2'
import PortfolioDisplay from './PortfolioDisplay'

interface Project {
  id: string
  projectName: string
  clientId: string
  urlStatus: string
  url: string
  websiteUrl: string
  startDate: string
  client: string
  pmPl: any[]
  industry: string
  service: Array<{id: number; vName: string}>
  technology: Array<{id: number; vName: string}>
  framework: Array<{id: number; vName: string}>
  eNDAprotected: string
  dHealthCheckedDate: string
  responseTime: string
  portfolio: any
  [key: string]: any
}

interface PortfolioContainerProps {
  projectIds: string[]
  onGenerateLink?: () => void
  isExportLoading?: boolean
  downloadLink?: string
  showButtons?: boolean
  projects?: Project[]
  isLoading?: boolean
  isPublic?: boolean
}

const PortfolioContainer: React.FC<PortfolioContainerProps> = ({
  projectIds,
  onGenerateLink,
  isExportLoading = false,
  downloadLink = '',
  showButtons = true,
  projects: providedProjects,
  isLoading: providedIsLoading,
  isPublic = false,
}) => {
  const [projects, setProjects] = useState<Project[]>(providedProjects || [])
  const [isLoading, setIsLoading] = useState(providedIsLoading !== undefined ? providedIsLoading : true)
  const overviewUrl = process.env.REACT_APP_OVERVIEW_URL

  useEffect(() => {
    // Skip API call if projects are already provided
    if (providedProjects) {
      return
    }

    // Try to get project IDs from session storage first (set by PortFolioWrapper)
    const storedProjects = sessionStorage.getItem('previewProjects')
    if (storedProjects) {
      const ids = JSON.parse(storedProjects)
      // Set projectIds and proceed with normal fetch
      if (ids.length > 0) {
        // Update the projectIds state and continue with normal flow
        const fetchProjectsWithStoredIds = async () => {
          if (ids.length === 0) {
            setIsLoading(false)
            return
          }

          setIsLoading(true)
          try {
            const payload = {
              ...initialQueryRequest.state,
              filter: {
                project: ids,
              },
              isExport: isPublic,
            }

            const response = await getDownloadLink(payload)
            // Handle response...
            setProjects(response.data || [])
          } catch (error) {
            console.error('Failed to fetch projects', error)
            Swal.fire('Error', 'Failed to load projects', 'error')
          } finally {
            setIsLoading(false)
          }
        }
        
        fetchProjectsWithStoredIds()
      } else {
        setIsLoading(false)
      }
      return
    }

    const fetchProjects = async () => {
      if (projectIds?.length === 0) {
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      try {
        const payload = {
          ...initialQueryRequest.state,
          filter: {
            project: projectIds,
          },
          isExport: isPublic,
        }

        const response = await getDownloadLink(payload)

        // Handle different response formats
        let projectsArray: Project[] = []

        if (response.data && Array.isArray(response.data)) {
          projectsArray = response.data
        } else if (response.projects && Array.isArray(response.projects)) {
          projectsArray = response.projects
        } else if (response.data && typeof response.data === 'object') {
          // If data is an object with project IDs as keys, convert to array
          projectsArray = Object.values(response.data)
        } else if (response.html_content) {
          // Parse HTML to extract project data
          const parser = new DOMParser()
          const doc = parser.parseFromString(response.html_content, 'text/html')
          const projectCards = doc.querySelectorAll('.project-card[data-project-id]')

          const extractedProjects: Project[] = []
          projectCards.forEach((card) => {
            const projectId = card.getAttribute('data-project-id')
            const titleElement = card.querySelector('.project-title')
            const websiteElement = card.querySelector('.field-value') // This might need adjustment based on actual HTML structure

            if (projectId && titleElement) {
              extractedProjects.push({
                id: projectId,
                projectName: titleElement.textContent?.trim() || `Project ${projectId}`,
                clientId: '',
                urlStatus: '',
                url: websiteElement?.textContent?.trim() || '-',
                websiteUrl: '',
                startDate: '',
                client: '',
                pmPl: [],
                industry: '',
                service: [],
                technology: [],
                framework: [],
                eNDAprotected: '',
                dHealthCheckedDate: '',
                responseTime: '',
                portfolio: null,
              })
            }
          })

          setProjects(extractedProjects)
        } else {
          // Unexpected response structure
        }

        setProjects(projectsArray)
      } catch (error) {
        console.error('Failed to fetch projects', error)
        Swal.fire('Error', 'Failed to load projects', 'error')
      } finally {
        setIsLoading(false)
      }
    }

    fetchProjects()
  }, [projectIds, providedProjects, isPublic])

  const handleCopyLink = () => {
    if (downloadLink) {
      navigator.clipboard.writeText(downloadLink)
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Link copied to clipboard',
        showConfirmButton: false,
        timer: 3000,
      })
    }
  }

  const handleEditProject = (projectId: string) => {
    window.open(`${overviewUrl}/project-management/projects/edit/${projectId}/general`, '_blank')
    // window.open(
    //   `http://localhost:5173/project-management/projects/edit/${projectId}/general`,
    //   '_blank'
    // )
  }

  if (isLoading) {
    return (
      <div className='d-flex flex-column align-items-center justify-content-center vh-100'>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
        <div className='mt-3'>Loading portfolio...</div>
      </div>
    )
  }

  if (projects.length === 0) {
    return (
      <div className='d-flex align-items-center justify-content-center vh-100'>
        <div className='alert alert-warning'>No projects found.</div>
      </div>
    )
  }

  return (
    <PortfolioDisplay
      projects={projects}
      showButtons={showButtons}
      onGenerateLink={onGenerateLink}
      isExportLoading={isExportLoading}
      downloadLink={downloadLink}
      onCopyLink={handleCopyLink}
      onEditProject={handleEditProject}
    />
  )
}

export default PortfolioContainer
